import { ABILITY_SLOTS } from '@/data/abilities';
import { MAX_WEAPON_LEVEL, WEAPONS, type WeaponId, isWeaponId } from '@/data/weapons';
import type { ToolBelt } from '@/world/toolbelt';
import type { WeaponAbilities } from '@/world/weaponAbilities';
import type { WeaponGrowth } from '@/world/weaponGrowth';

/**
 * Merging weapons, and what they become.
 *
 * Two operations that both consume a blade, which is what makes them worth
 * thinking about rather than obviously worth doing.
 *
 * **Synthesis** feeds one weapon into another: the survivor takes half of what
 * the sacrifice had learned, and any abilities it has room for. Half rather
 * than all, so synthesis is a way to *carry* progress rather than a way to
 * lose nothing — a blade you switch away from is still worth something, but
 * not everything.
 *
 * **Evolution** turns a weapon that has gone as far as it can into a new one.
 * It costs the original and the levels, and keeps the abilities, so the thing
 * you give up is the practice and the thing you keep is the character.
 */

export type SynthesisResult =
  | 'synthesised'
  | 'same-weapon'
  | 'not-carried'
  | 'nothing-to-give';

export type EvolutionResult = 'evolved' | 'not-carried' | 'cannot-evolve' | 'not-ready';

export interface ForgeState {
  readonly belt: ToolBelt;
  readonly growth: WeaponGrowth;
  readonly abilities: WeaponAbilities;
}

/** How much of the sacrifice's practice survives the merge. */
export const SYNTHESIS_TRANSFER = 0.5;

function carries(belt: ToolBelt, weapon: WeaponId): boolean {
  return belt.all().some((tool) => tool.id === weapon);
}

export function synthesisBlocked(
  target: WeaponId,
  source: WeaponId,
  { belt, growth, abilities }: ForgeState,
): Exclude<SynthesisResult, 'synthesised'> | null {
  if (target === source) return 'same-weapon';
  if (!carries(belt, target) || !carries(belt, source)) return 'not-carried';

  // Refused rather than allowed as a no-op: destroying a weapon that had
  // nothing to give is the worst possible outcome of pressing a button.
  const givesExperience = growth.experienceFor(source) > 0;
  const givesAbility = abilities
    .of(source)
    .some((ability) => !abilities.has(target, ability));

  if (!givesExperience && !givesAbility) return 'nothing-to-give';

  return null;
}

export function synthesise(
  target: WeaponId,
  source: WeaponId,
  state: ForgeState,
): SynthesisResult {
  const blocked = synthesisBlocked(target, source, state);
  if (blocked) return blocked;

  const { belt, growth, abilities } = state;

  growth.gain(target, Math.floor(growth.experienceFor(source) * SYNTHESIS_TRANSFER));

  for (const ability of abilities.of(source)) {
    if (abilities.spareSlots(target) <= 0) break;
    if (abilities.has(target, ability)) continue;
    abilities.grant(target, ability);
  }

  abilities.forget(source);
  growth.forget(source);
  belt.discard(source);

  return 'synthesised';
}

export function evolutionBlocked(
  weapon: WeaponId,
  { belt, growth, abilities }: ForgeState,
): Exclude<EvolutionResult, 'evolved'> | null {
  if (!carries(belt, weapon)) return 'not-carried';

  const next = WEAPONS[weapon].evolvesTo;
  if (!next || !isWeaponId(next)) return 'cannot-evolve';

  // Fully grown and fully realised. Both, because either alone would make
  // evolution something that happens to you rather than something you did.
  if (growth.levelFor(weapon) < MAX_WEAPON_LEVEL) return 'not-ready';
  if (abilities.of(weapon).length < ABILITY_SLOTS) return 'not-ready';

  return null;
}

export function evolve(weapon: WeaponId, state: ForgeState): EvolutionResult {
  const blocked = evolutionBlocked(weapon, state);
  if (blocked) return blocked;

  const { belt, growth, abilities } = state;
  const next = WEAPONS[weapon].evolvesTo!;

  belt.acquire(next);

  // Abilities carry; levels do not. The new blade is a new blade, and earning
  // its practice again is the price of the better one.
  for (const ability of abilities.of(weapon)) abilities.grant(next, ability);

  abilities.forget(weapon);
  growth.forget(weapon);
  belt.discard(weapon);
  belt.setActive(next);

  return 'evolved';
}
